import React from 'react';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
import { getFigurineImageSrc } from '../utils/figurineMedia';

export default function CartDrawer({ isOpen, onClose }) {
  const {
    cartItems,
    removeFromCart,
    clearCart,
    total,
    incrementQuantity,
    decrementQuantity,
  } = useCart();

  return (
    <>
      <button
        type="button"
        aria-label="Close cart"
        onClick={onClose}
        className={`fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm transition-opacity ${
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      <aside
        className={`fixed right-0 top-0 z-[70] flex h-full w-full max-w-sm transform flex-col border-l border-blue-400/30 bg-[#0f1524] text-white shadow-2xl transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!isOpen}
      >
        <div className="flex items-center justify-between border-b border-blue-400/20 p-5">
          <div>
            <p className="nfig-kicker">Your selection</p>
            <h2 className="text-2xl font-black uppercase tracking-wide">Cart</h2>
          </div>
          <button onClick={onClose} className="nfig-close static" aria-label="Close cart">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {cartItems.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-4 h-16 w-16 rounded-full border border-blue-400/20 bg-blue-400/10" />
              <p className="font-bold text-slate-200">Your cart is empty</p>
              <p className="mt-1 text-sm text-slate-500">Find your next figure in the collection.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item, index) => (
                <article key={`${item.id || item.name}-${index}`} className="rounded-xl border border-blue-400/15 bg-white/[0.035] p-3">
                  <div className="flex gap-3">
                    <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg bg-slate-100 p-1">
                      <img src={getFigurineImageSrc(item)} alt={item.name} className="h-full w-full object-contain" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-bold">{item.name}</p>
                      <p className="truncate text-xs text-slate-500">{item.series}</p>
                      {item.stock === 0 && <p className="mt-1 text-xs font-bold uppercase text-red-300">Pre-order</p>}
                      <div className="mt-2 flex items-center justify-between">
                        <p className="font-black text-blue-300">₪{item.value}</p>
                        <button onClick={() => removeFromCart(index)} className="text-xs text-slate-500 transition hover:text-red-300">Remove</button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-3 border-t border-white/5 pt-3">
                    <button onClick={() => decrementQuantity(index)} className="flex h-8 w-8 items-center justify-center rounded border border-white/10 bg-white/5 hover:border-blue-400/50">−</button>
                    <span className="min-w-5 text-center font-bold">{item.quantity}</span>
                    <button onClick={() => incrementQuantity(index)} className="flex h-8 w-8 items-center justify-center rounded border border-white/10 bg-white/5 hover:border-blue-400/50">+</button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-blue-400/20 bg-[#0b1020] p-5">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm uppercase tracking-widest text-slate-400">Total</span>
            <span className="text-2xl font-black">₪{total}</span>
          </div>
          <Link to="/cart" onClick={onClose} className="nfig-button-primary w-full">View Cart</Link>
          {cartItems.length > 0 && (
            <button onClick={clearCart} className="mt-2 w-full py-2 text-xs text-slate-500 transition hover:text-red-300">Clear cart</button>
          )}
        </div>
      </aside>
    </>
  );
}
